import React, { useState } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useFavorites } from '../contexts/FavoritesContext';
import { ScoreCalculatorModal } from '../components/ScoreCalculatorModal';
import { 
  Brain, 
  MagnifyingGlass, 
  Lightning, 
  Pill, 
  Star,
  Calculator,
  X,
  CurrencyEur,
  CheckCircle,
  BookOpen,
  Stethoscope,
  ArrowRight,
  Eye,
  ClockCounterClockwise
} from '@phosphor-icons/react';

export const DashboardPage = () => {
  const navigate = useNavigate();
  const { favorites, removeFavorite } = useFavorites();
  const [modalScoreId, setModalScoreId] = useState(null);

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-[#0A0A0A] p-6 md:p-8">
      {/* Hero Section with Background */}
      <div 
        className="relative rounded-2xl overflow-hidden mb-8"
        style={{
          backgroundImage: `url('https://images.unsplash.com/photo-1614850523011-8f49ffc73908?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGRhcmslMjBibHVlJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODE2ODA0MDJ8MA&ixlib=rb-4.1.0&q=85')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-black/70"></div>
        <div className="relative z-10 p-8 md:p-12">
          <div className="max-w-2xl">
            {/* Overline */}
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-blue-400 mb-4">
              Outil Documentaire Médical
            </p>
            
            {/* Title */}
            <h1 className="text-4xl sm:text-5xl tracking-tight leading-tight font-bold text-white mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Fiche Diagnostic
            </h1>
            
            {/* Subtitle */}
            <p className="text-zinc-400 text-lg mb-8 leading-relaxed">
              Référence documentaire basée sur les sources officielles : HAS, Collèges des Enseignants, ANSM et recommandations françaises.
            </p>
            
            {/* CTA Button */}
            <button
              data-testid="quick-diagnosis-button"
              onClick={() => navigate('/diagnosis')}
              className="inline-flex items-center gap-3 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_40px_-10px_rgba(37,99,235,0.5)]"
            >
              <Stethoscope size={24} weight="duotone" />
              <span>Rechercher une Information</span>
              <ArrowRight size={20} weight="bold" />
            </button>
          </div>
        </div>
      </div>

      {/* Quick Actions - tous les services */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Fiche Diagnostic */}
        <button
          data-testid="action-diagnostic"
          onClick={() => navigate('/diagnosis')}
          className="bg-[#121212] border border-white/10 rounded-lg p-6 h-40 text-left transition-all duration-200 hover:-translate-y-1 hover:border-blue-500/50 group"
        >
          <Brain size={40} weight="duotone" className="text-blue-500 mb-4 group-hover:text-blue-400 transition-colors" />
          <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-blue-400 transition-colors" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Fiche Diagnostic
          </h3>
          <p className="text-sm text-zinc-500">Référence documentaire IA</p>
        </button>

        {/* Recherche Ciblée */}
        <button
          data-testid="action-recherche"
          onClick={() => navigate('/medical-search')}
          className="bg-[#121212] border border-white/10 rounded-lg p-6 h-40 text-left transition-all duration-200 hover:-translate-y-1 hover:border-emerald-500/50 group"
        >
          <MagnifyingGlass size={40} weight="duotone" className="text-emerald-500 mb-4 group-hover:text-emerald-400 transition-colors" />
          <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-emerald-400 transition-colors" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Recherche Ciblée
          </h3>
          <p className="text-sm text-zinc-500">Sources officielles françaises</p>
        </button>

        {/* Dermato - Référentiel */}
        <button
          data-testid="action-dermato"
          onClick={() => navigate('/dermato')}
          className="bg-[#121212] border border-white/10 rounded-lg p-6 h-40 text-left transition-all duration-200 hover:-translate-y-1 hover:border-rose-500/50 group"
        >
          <Eye size={40} weight="duotone" className="text-rose-500 mb-4 group-hover:text-rose-400 transition-colors" />
          <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-rose-400 transition-colors" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Dermato
          </h3>
          <p className="text-sm text-zinc-500">Analyse d'image DermNet NZ</p>
        </button>

        {/* Scores Médicaux */}
        <button
          data-testid="action-scores"
          onClick={() => navigate('/scores')}
          className="bg-[#121212] border border-white/10 rounded-lg p-6 h-40 text-left transition-all duration-200 hover:-translate-y-1 hover:border-violet-500/50 group"
        >
          <Calculator size={40} weight="duotone" className="text-violet-500 mb-4 group-hover:text-violet-400 transition-colors" />
          <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-violet-400 transition-colors" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Scores Médicaux
          </h3>
          <p className="text-sm text-zinc-500">55 calculateurs cliniques</p>
        </button>

        {/* Médicaments BDPM */}
        <button
          data-testid="action-medicaments"
          onClick={() => navigate('/medications')}
          className="bg-[#121212] border border-white/10 rounded-lg p-6 h-40 text-left transition-all duration-200 hover:-translate-y-1 hover:border-amber-500/50 group"
        >
          <Pill size={40} weight="duotone" className="text-amber-500 mb-4 group-hover:text-amber-400 transition-colors" />
          <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-amber-400 transition-colors" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Médicaments - BDPM
          </h3>
          <p className="text-sm text-zinc-500">Base de données publique</p>
        </button>

        {/* Cotation CCAM/NGAP */}
        <button
          data-testid="action-cotation"
          onClick={() => navigate('/cotation')}
          className="bg-[#121212] border border-white/10 rounded-lg p-6 h-40 text-left transition-all duration-200 hover:-translate-y-1 hover:border-teal-500/50 group"
        >
          <CurrencyEur size={40} weight="duotone" className="text-teal-500 mb-4 group-hover:text-teal-400 transition-colors" />
          <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-teal-400 transition-colors" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Cotation CCAM/NGAP
          </h3>
          <p className="text-sm text-zinc-500">Tarifs et majorations 2025</p>
        </button>

        {/* Bibliographie */}
        <button
          data-testid="action-biblio"
          onClick={() => navigate('/biblio')}
          className="bg-[#121212] border border-white/10 rounded-lg p-6 h-40 text-left transition-all duration-200 hover:-translate-y-1 hover:border-indigo-500/50 group"
        >
          <BookOpen size={40} weight="duotone" className="text-indigo-500 mb-4 group-hover:text-indigo-400 transition-colors" />
          <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-indigo-400 transition-colors" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Bibliographie
          </h3>
          <p className="text-sm text-zinc-500">Références et documents</p>
        </button>

        {/* Historique */}
        <button
          data-testid="action-historique"
          onClick={() => navigate('/history')}
          className="bg-[#121212] border border-white/10 rounded-lg p-6 h-40 text-left transition-all duration-200 hover:-translate-y-1 hover:border-cyan-500/50 group"
        >
          <ClockCounterClockwise size={40} weight="duotone" className="text-cyan-500 mb-4 group-hover:text-cyan-400 transition-colors" />
          <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Historique
          </h3>
          <p className="text-sm text-zinc-500">Vos recherches passées</p>
        </button>
      </div>

      {/* Favorite Scores Widget */}
      {favorites.length > 0 && (
        <div 
          data-testid="favorites-widget"
          className="bg-[#121212] border border-white/10 rounded-lg p-6 mb-8"
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Star size={24} weight="fill" className="text-amber-400" />
              <h2 className="text-xl font-semibold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Mes Scores Favoris
              </h2>
              <span className="text-xs bg-amber-500/20 text-amber-400 px-2 py-1 rounded-full font-medium">
                {favorites.length}/10
              </span>
            </div>
            <button
              data-testid="view-all-scores"
              onClick={() => navigate('/scores')}
              className="text-sm text-blue-500 hover:text-blue-400 transition-colors flex items-center gap-1"
            >
              Voir tous les scores
              <ArrowRight size={16} weight="bold" />
            </button>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {favorites.map((score) => (
              <div
                key={score.id}
                data-testid={`dashboard-favorite-${score.id}`}
                className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 hover:border-amber-500/30 transition-all duration-200 group"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <h3 className="text-white font-semibold text-sm">{score.name}</h3>
                    <p className="text-zinc-500 text-xs truncate">{score.categoryName}</p>
                  </div>
                  <button
                    data-testid={`remove-favorite-${score.id}`}
                    onClick={() => {
                      removeFavorite(score.id);
                      toast.success('Score retiré des favoris');
                    }}
                    className="p-1 text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all"
                    title="Retirer des favoris"
                  >
                    <X size={16} weight="bold" />
                  </button>
                </div>
                
                {score.cotation && (
                  <div className="flex items-center gap-1 text-xs text-emerald-400 mb-3">
                    <CurrencyEur size={14} weight="bold" />
                    <span>{score.cotation.tarif}€</span>
                  </div>
                )}
                
                <button
                  data-testid={`quick-calc-${score.id}`}
                  onClick={() => setModalScoreId(score.id)}
                  className="w-full py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-all flex items-center justify-center gap-2"
                >
                  <Calculator size={14} weight="bold" />
                  Calculer
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Sources Section */}
      <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
        <div className="text-center mb-8">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-500 mb-2">
            Sources Certifiées
          </p>
          <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>
            Recommandations Officielles Françaises
          </h2>
        </div>
        
        {/* Main Sources - Horizontal flex */}
        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
          {/* HAS */}
          <div data-testid="source-has" className="flex flex-col items-center group cursor-default">
            <div className="w-20 h-20 rounded-xl bg-blue-900/30 border border-blue-500/30 flex items-center justify-center mb-2 group-hover:border-blue-500/60 transition-colors">
              <span className="text-2xl font-bold text-blue-400">HAS</span>
            </div>
            <p className="text-xs text-zinc-500 text-center">Haute Autorité<br/>de Santé</p>
          </div>

          {/* Ameli */}
          <div data-testid="source-ameli" className="flex flex-col items-center group cursor-default">
            <div className="w-20 h-20 rounded-xl bg-emerald-900/30 border border-emerald-500/30 flex items-center justify-center mb-2 group-hover:border-emerald-500/60 transition-colors">
              <span className="text-xl font-bold text-emerald-400">Ameli</span>
            </div>
            <p className="text-xs text-zinc-500 text-center">Assurance<br/>Maladie</p>
          </div>

          {/* Collèges FR */}
          <div data-testid="source-colleges" className="flex flex-col items-center group cursor-default">
            <div className="w-20 h-20 rounded-xl bg-indigo-900/30 border border-indigo-500/30 flex items-center justify-center mb-2 group-hover:border-indigo-500/60 transition-colors">
              <BookOpen size={32} weight="duotone" className="text-indigo-400" />
            </div>
            <p className="text-xs text-zinc-500 text-center">Collèges des<br/>Enseignants</p>
          </div>

          {/* BDPM */}
          <div data-testid="source-bdpm" className="flex flex-col items-center group cursor-default">
            <div className="w-20 h-20 rounded-xl bg-purple-900/30 border border-purple-500/30 flex items-center justify-center mb-2 group-hover:border-purple-500/60 transition-colors">
              <span className="text-xl font-bold text-purple-400">BDPM</span>
            </div>
            <p className="text-xs text-zinc-500 text-center">Base Publique<br/>Médicaments</p>
          </div>

          {/* CISMeF */}
          <div data-testid="source-cismef" className="flex flex-col items-center group cursor-default">
            <div className="w-20 h-20 rounded-xl bg-cyan-900/30 border border-cyan-500/30 flex items-center justify-center mb-2 group-hover:border-cyan-500/60 transition-colors">
              <span className="text-lg font-bold text-cyan-400">CISMeF</span>
            </div>
            <p className="text-xs text-zinc-500 text-center">Catalogue<br/>Médical</p>
          </div>
        </div>

        {/* Secondary Sources */}
        <div className="mt-8 pt-6 border-t border-white/5">
          <div className="flex flex-wrap justify-center gap-4 text-xs text-zinc-600">
            <span className="flex items-center gap-2 hover:text-zinc-400 transition-colors">
              <CheckCircle size={12} weight="fill" className="text-emerald-600" />
              Antibioclic
            </span>
            <span className="flex items-center gap-2 hover:text-zinc-400 transition-colors">
              <CheckCircle size={12} weight="fill" className="text-blue-600" />
              CRAT
            </span>
            <span className="flex items-center gap-2 hover:text-zinc-400 transition-colors">
              <CheckCircle size={12} weight="fill" className="text-rose-600" />
              Orphanet
            </span>
            <span className="flex items-center gap-2 hover:text-zinc-400 transition-colors">
              <CheckCircle size={12} weight="fill" className="text-pink-600" />
              SFDermato
            </span>
            <span className="flex items-center gap-2 hover:text-zinc-400 transition-colors">
              <CheckCircle size={12} weight="fill" className="text-orange-600" />
              DermNet NZ
            </span>
            <span className="flex items-center gap-2 hover:text-zinc-400 transition-colors">
              <CheckCircle size={12} weight="fill" className="text-amber-600" />
              PubMed
            </span>
            <span className="flex items-center gap-2 hover:text-zinc-400 transition-colors">
              <CheckCircle size={12} weight="fill" className="text-violet-600" />
              Sociétés Savantes
            </span>
          </div>
        </div>
      </div>

      {/* Score Calculator Modal */}
      <ScoreCalculatorModal
        isOpen={!!modalScoreId}
        onClose={() => setModalScoreId(null)}
        scoreId={modalScoreId}
      />
    </div>
  );
};
